import {
  autoSignIn as amplifyAutoSignIn,
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  confirmSignIn,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  fetchAuthSession,
  fetchUserAttributes,
  signOut as amplifySignOut,
} from "aws-amplify/auth";

import { oppositeGroup, requiredGroup } from "../config/cognito";

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Result of step 1 (initiate). When `mode === "otp"` the FE should show the
 * OTP entry field and call `submitOtp()` next. When `mode === "done"` the
 * sign-in is already complete (e.g. the user had a recent valid session) and
 * the FE can move straight to the Cognito → Penn JWT exchange.
 */
export type InitiateResult = { mode: "otp" } | { mode: "done" };

export async function initiateOtpSignIn(
  email: string,
): Promise<InitiateResult> {
  const username = normalizeEmail(email);

  // Recover from "There is already a signed in user" REACTIVELY (ADR-0036).
  // This used to call `ensureSignedOut()` unconditionally, which meant merely
  // OPENING /login in one tab destroyed the Cognito session for the whole
  // browser profile — and the Resend button did it again. The pre-emptive
  // sign-out only ever existed to dodge this one error, so handle the error.
  let out;
  try {
    out = await amplifySignIn({
      username,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    });
  } catch (err) {
    if (!isAlreadySignedInError(err)) throw err;
    await ensureSignedOut();
    out = await amplifySignIn({
      username,
      options: {
        authFlowType: "USER_AUTH",
        preferredChallenge: "EMAIL_OTP",
      },
    });
  }
  if (out.isSignedIn) return { mode: "done" };
  if (out.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
    return { mode: "otp" };
  }
  // Any other nextStep is unexpected for our pool config — surface clearly.
  throw new Error(
    `Unexpected Cognito next step: ${out.nextStep?.signInStep ?? "<none>"}`,
  );
}

/**
 * Re-trigger an OTP send for the same email by signing out and re-initiating
 * the OTP flow. Cognito does not expose a dedicated "resend" API for the
 * USER_AUTH/EMAIL_OTP flow — the canonical path is to start the sign-in
 * over, which causes the Custom Email Sender Lambda to mint and email a
 * fresh code. The previous in-flight challenge is invalidated by the new
 * sign-in.
 */
export async function resendOtp(email: string): Promise<InitiateResult> {
  // Resend genuinely needs the restart (Cognito has no resend API for
  // USER_AUTH/EMAIL_OTP), but only the CHALLENGE has to be abandoned — there is
  // no signed-in session to drop mid-OTP, so no `ensureSignedOut()` here
  // either.
  return initiateOtpSignIn(email);
}

function isAlreadySignedInError(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? "";
  const message = (err as { message?: string } | null)?.message ?? "";
  return (
    name === "UserAlreadyAuthenticatedException" ||
    /already\s+a?\s*signed[- ]in user/i.test(message)
  );
}

/**
 * Best-effort sign-out used as a guard before signIn(). Swallows
 * NotAuthorizedException / no-current-user errors — the goal is "no live
 * session afterwards", not "there was one".
 */
async function ensureSignedOut(): Promise<void> {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    if (session.tokens?.accessToken) {
      await amplifySignOut();
    }
  } catch {
    // No session — fine.
  }
}

export async function submitOtp(otp: string): Promise<void> {
  const out = await confirmSignIn({ challengeResponse: otp });
  if (!out.isSignedIn) {
    throw new Error("OTP did not complete sign-in. Please try again.");
  }
}

/**
 * Read the current Cognito ID token. Called immediately after `submitOtp()`
 * to feed the Penn-JWT exchange — the backend binds on the ID token's `email`
 * claim (access tokens carry no email). `forceRefresh=false`: the OTP step
 * just minted a fresh token, so a forced refresh would burn a roundtrip.
 */
export async function currentCognitoIdToken(): Promise<string> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Cognito session has no ID token.");
  return token;
}

/**
 * Silent refresh — used by the AxiosService 401 interceptor before
 * redirecting to /login. Force-refreshes the underlying Cognito session and
 * returns the new ID token. Returns null if there's no live session.
 */
export async function refreshCognitoIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Cross-app gate (ADR-0010 §3.4). Customer SPA requires the `customer` group;
 * admin SPA requires the `admin` group. Returns true when the user belongs
 * to the SPA's required group; false otherwise. The backend re-enforces
 * this at the exchange — this is just for fast UX.
 */
export async function inRequiredCognitoGroup(): Promise<boolean> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const claim = session.tokens?.idToken?.payload["cognito:groups"];
  const groups = Array.isArray(claim)
    ? claim.filter((g): g is string => typeof g === "string")
    : [];
  if (groups.includes(requiredGroup())) return true;
  // Empty groups happen on the very first OTP login — accept (the backend
  // safety net handles it). Opposite group means wrong-app.
  if (groups.includes(oppositeGroup())) return false;
  return true;
}

export interface SessionEmail {
  email: string;
}

export async function fetchSessionEmail(): Promise<SessionEmail | null> {
  try {
    const attrs = await fetchUserAttributes();
    const email = attrs.email;
    if (!email) return null;
    return { email: normalizeEmail(email) };
  } catch {
    return null;
  }
}

export interface CognitoSignUpArgs {
  email: string;
  /** Throwaway password — pool is passwordless, but Amplify's signUp() API
   *  still requires one. Generated by the caller (random) and never stored. */
  password: string;
  /** Additional Cognito user attributes (name, family_name, …). */
  userAttributes?: Record<string, string>;
}

export interface CognitoSignUpResult {
  cognitoSub: string;
}

/**
 * Create the Cognito user. The pre-sign-up Lambda does NOT auto-confirm the
 * web/customer client, so Cognito returns a CONFIRM_SIGN_UP step and emails the
 * signup-branded code. Amplify retains a same-tab USER_AUTH continuation so a
 * promptly confirmed user can be signed in without a second OTP.
 */
export async function cognitoSignUp(
  args: CognitoSignUpArgs,
): Promise<CognitoSignUpResult> {
  const email = normalizeEmail(args.email);
  const out = await amplifySignUp({
    username: email,
    password: args.password,
    options: {
      userAttributes: {
        email,
        ...(args.userAttributes ?? {}),
      },
      autoSignIn: { authFlowType: "USER_AUTH" },
    },
  });
  const sub = out.userId;
  if (!sub) {
    throw new Error(
      "Cognito did not return a userId on signUp; cannot bind local user record.",
    );
  }
  return { cognitoSub: sub };
}

export async function cognitoSignOut(): Promise<void> {
  try {
    await amplifySignOut();
  } catch {
    // best-effort
  }
}

/**
 * Confirm a just-created Cognito sign-up with the emailed verification code.
 * Amplify reports whether its same-tab auto-sign-in continuation is still
 * available. The caller keeps the existing manual-login fallback when it is
 * not available (for example after Amplify's short continuation window).
 */
export type ConfirmSignUpContinuation =
  | "complete_auto_sign_in"
  | "manual_sign_in";

export interface ConfirmSignUpResult {
  continuation: ConfirmSignUpContinuation;
}

export async function confirmSignUpCode(
  email: string,
  code: string,
): Promise<ConfirmSignUpResult> {
  const out = await amplifyConfirmSignUp({
    username: normalizeEmail(email),
    confirmationCode: code,
  });
  if (!out.isSignUpComplete) {
    throw new Error("Email verification did not complete. Please try again.");
  }
  return {
    continuation:
      out.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN"
        ? "complete_auto_sign_in"
        : "manual_sign_in",
  };
}

export interface CompleteAutoSignInResult {
  signedIn: boolean;
}

/**
 * Complete Amplify's registration continuation. Unexpected/expired
 * continuations are normal fallback conditions, not registration failures.
 */
export async function completeAutoSignIn(): Promise<CompleteAutoSignInResult> {
  try {
    const out = await amplifyAutoSignIn();
    return {
      signedIn: out.isSignedIn === true || out.nextStep?.signInStep === "DONE",
    };
  } catch {
    return { signedIn: false };
  }
}

/**
 * Re-send the SIGN-UP verification code (option 2 confirm step). Distinct from
 * `resendOtp()`, which re-initiates a SIGN-IN OTP; this re-issues the SIGN-UP
 * confirmation code via Cognito's ResendConfirmationCode.
 */
export async function resendSignUpCode(email: string): Promise<void> {
  await amplifyResendSignUpCode({ username: normalizeEmail(email) });
}

/**
 * Generate a sufficiently strong throwaway password for Amplify's signUp()
 * API. The pool is passwordless — no one ever uses it — but Cognito requires
 * the field to satisfy the pool's password policy. 32 bytes of base64 lands
 * us a string that comfortably exceeds any reasonable Cognito policy.
 */
export function newThrowawayPassword(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  // base64 + a fixed suffix to guarantee a digit + special-char if the pool
  // policy demands it.
  return btoa(s).replace(/[+/=]/g, "x") + "Aa1!";
}
